import React from 'react';
import { css } from '@emotion/core';
import ChevronLeft from '@atlaskit/icon/glyph/chevron-left';
import Badge from '@atlaskit/badge';
import { CapabilityDTO } from '@atlassiansox/phobos-client-ts';
import { token } from '@atlaskit/tokens';

import {
  colors,
  grid,
  cursorPointer,
  flexStart,
  alignItemsCenter,
  spaceGapCozy,
  spaceGapMinimum,
  spaceGapComfortable,
} from '@/styles';
import {
  ActionButtons,
  ProductEntityLink,
  TeamEntityLink,
  KeyValue,
  CapabilityTier,
  BreachCount,
  CapabilityEntityLink,
} from '@/components/primitive';
import { MiniSlos } from '@/components/composed';
import { PrimaryType } from '@/meta';

import * as styles from './styles';

type CapabilityListItemProps = Pick<
  CapabilityDTO,
  | 'publicId'
  | 'name'
  | 'product'
  | 'team'
  | 'importance'
  | 'breaches'
  | 'sloCount'
  | 'qualityIssues'
> & {
  onSelect: () => void;
  isSelected: boolean;
  entityType?: PrimaryType;
};

const CapabilityListItem: React.FC<CapabilityListItemProps> = ({
  publicId,
  name,
  team,
  product,
  breaches,
  sloCount,
  importance,
  isSelected,
  entityType,
  qualityIssues,
  onSelect,
}) => (
  <div
    css={[
      styles.tableRowOuterStyles,
      isSelected ? styles.tableRowSelectedStyles : '',
      entityType ? styles.tableBottomBorderStyles : styles.tableBorderStyles,
    ]}
  >
    <div
      key={publicId}
      className={isSelected ? 'capability selected' : 'capability'}
      css={styles.tableRowInnerStyles}
      data-testid="capability"
    >
      <div
        css={[
          grid,
          alignItemsCenter,
          spaceGapComfortable,
          css`
            grid-template-columns: max-content auto;
          `,
        ]}
      >
        <h4>
          <CapabilityEntityLink publicId={publicId} name={name} shouldTruncate />
        </h4>
        <div
          css={[
            css`
              display: grid;
              grid-template-columns: repeat(4, max-content);
            `,
            spaceGapCozy,
          ]}
        >
          <KeyValue title="Capability Tier">
            <CapabilityTier tier={importance} />
          </KeyValue>
          {entityType !== 'product' && (
            <KeyValue title="Product">
              <ProductEntityLink name={product.name} publicId={product.publicId} />
            </KeyValue>
          )}
          {entityType !== 'team' && (
            <KeyValue title="Team">
              <TeamEntityLink
                name={team.name}
                publicId={team.publicId}
                shouldTruncate
                truncateLength="350px"
              />
            </KeyValue>
          )}
          <div css={[flexStart, spaceGapCozy]}>
            <KeyValue title="Breaching SLOs">
              <BreachCount count={breaches} />
            </KeyValue>
            {entityType && (
              <KeyValue title="With quality issue SLOs">
                <BreachCount isWarning count={qualityIssues} />
              </KeyValue>
            )}
            <KeyValue title="Total">
              <Badge max={Infinity} appearance="default">
                {sloCount}
              </Badge>
            </KeyValue>
          </div>
        </div>
      </div>

      <div
        css={[
          grid,
          spaceGapMinimum,
          css`
            grid-template-columns: min-content min-content 1fr;
            align-items: center;
          `,
        ]}
      >
        <ActionButtons id={publicId} entityName={name} type="capability" hideOnBlur />
        <div
          onClick={onSelect}
          css={[
            cursorPointer,
            css`
              line-height: 1;
              &:hover,
              .selected & {
                color: ${token('color.text.selected', colors.B200)};
              }
              transition: transform 0.3s ease;
              transform: rotate(${isSelected ? -90 : 0}deg);
            `,
          ]}
        >
          <ChevronLeft size="medium" label="Toggle SLOs" />
        </div>
      </div>
    </div>
    <div>{isSelected && <MiniSlos id={publicId} />}</div>
  </div>
);

export default CapabilityListItem;
